import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Heart,
  Calendar,
  User,
  Tag,
  Eye,
  Share2
} from 'lucide-react';
import FavoriteButton from '../components/FavoriteButton';
import ShareButtons from '../components/ShareButtons';

interface FavoriteStatistic {
  id: string;
  statisticId: string;
  createdAt: string;
  statistic: {
    id: string;
    title: string;
    slug: string;
    lead?: string;
    imageId?: string;
    status: string;
    type: string;
  };
}

interface FavoritesResponse {
  favorites: FavoriteStatistic[];
  total: number;
}

const Favorites: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [, navigate] = useLocation();
  const [favorites, setFavorites] = useState<FavoriteStatistic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchFavorites();
  }, [isAuthenticated]);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/favorites/user', {
        credentials: 'include'
      });

      if (response.ok) {
        const data: FavoritesResponse = await response.json();
        setFavorites(data.favorites);
      } else if (response.status === 401) {
        navigate('/login');
      } else {
        setError('Failed to load favorites');
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
      setError('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  };

  const handleFavoriteToggle = (isFavorited: boolean, count: number) => {
    if (!isFavorited) {
      // Remove from favorites list - we need to find the statisticId from the current context
      // This will be handled by the individual FavoriteButton components
    }
  };

  if (!isAuthenticated) {
    return null; // Will redirect to login
  }

  if (loading) {
    return (
      <div className="statemia-container">
        <div className="statemia-card">
          <div className="text-center py-12">
            <div className="statemia-spinner mx-auto mb-4" />
            <p>{t('favorites.loadingFavorites')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="statemia-container">
        <div className="statemia-card">
          <div className="text-center py-12">
            <h3 className="text-xl font-semibold text-foreground mb-2">
              {t('favorites.errorLoadingFavorites')}
            </h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={fetchFavorites}
              className="btn-primary"
            >
              {t('common.tryAgain')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="statemia-container">
      {/* Header - temporarily hidden */}
      {/* <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="btn-secondary flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t('favorites.myFavorites')}</h1>
            <p className="text-muted-foreground">
              {favorites.length} {favorites.length === 1 ? t('favorites.savedStatistic') : t('favorites.savedStatistics')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Heart size={24} className="text-red-500" />
        </div>
      </div> */}

      {/* Favorites List */}
      {favorites.length === 0 ? (
        <div className="statemia-card mt-6">
          <div className="text-center py-12">
            <Heart size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              {t('favorites.noFavoritesYet')}
            </h3>
            <p className="text-muted-foreground mb-6">
              {t('favorites.saveFavoritesDescription')}
            </p>
            <button
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              {t('favorites.exploreStatistics')}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          {favorites.map((favorite) => (
            <div key={favorite.id} className="statemia-card group hover:shadow-lg transition-shadow">
              {/* Image */}
              {favorite.statistic.imageId && (
                <div className="mb-4">
                  <img
                    src={`/api/images/serve/${favorite.statistic.imageId}`}
                    alt={favorite.statistic.title}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* Content */}
              <div className="space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-2">
                    {favorite.statistic.title}
                  </h3>
                  {favorite.statistic.lead && (
                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {favorite.statistic.lead}
                    </p>
                  )}
                </div>

                {/* Metadata */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar size={12} />
                    <span>
                      {new Date(favorite.createdAt).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User size={12} />
                    <span>Periodista</span>
                  </div>
                </div>

                {/* Type Badge */}
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    favorite.statistic.type === 'curioseme'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {favorite.statistic.type === 'curioseme' ? 'Curioseme' : 'Estadística'}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-surface-200">
                  <div className="flex items-center gap-2">
                    <FavoriteButton
                      statisticId={favorite.statisticId}
                      onToggle={handleFavoriteToggle}
                      size="sm"
                      showCount={false}
                    />
                    <ShareButtons
                      url={`${window.location.origin}/statistics/${favorite.statistic.slug}`}
                      title={favorite.statistic.title}
                      description={favorite.statistic.lead || ''}
                      variant="compact"
                    />
                  </div>
                  <button
                    onClick={() => navigate(`/statistics/${favorite.statistic.slug}`)}
                    className="btn-secondary text-sm"
                  >
                    Ver
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Favorites; 